class VxspeedTheme {
  static init(scope = document) {
    this.bindDisclosureMenus(scope);
    this.bindVehicleFinders(scope);
    this.bindFilterForms(scope);
    this.bindTimezoneOffsets(scope);
    this.bindComparisons(scope);
    window.Shopify?.PaymentButton?.init?.();
  }

  static bindDisclosureMenus(scope) {
    scope.querySelectorAll('[data-mobile-menu-toggle]:not([data-theme-bound])').forEach((button) => {
      button.dataset.themeBound = 'true';
      button.addEventListener('click', () => {
        const menu = document.getElementById(button.getAttribute('aria-controls'));
        if (!menu) return;
        const isOpen = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!isOpen));
        menu.hidden = isOpen;
      });
    });
  }

  static bindVehicleFinders(scope) {
    scope.querySelectorAll('[data-vehicle-finder]:not([data-theme-bound])').forEach((form) => {
      form.dataset.themeBound = 'true';
      const make = form.querySelector('[name="vehicle_make"]');
      const model = form.querySelector('[name="vehicle_model"]');
      const year = form.querySelector('[name="vehicle_year"]');
      const output = form.querySelector('[data-finder-status]');
      form.addEventListener('submit', (event) => {
        if (!make?.value || !model?.value || !year?.value) return;
        const destination = model.selectedOptions[0]?.dataset.url;
        event.preventDefault();
        if (destination) {
          window.location.assign(destination);
        } else if (output) {
          output.textContent = form.dataset.unverifiedMessage || '';
          output.hidden = false;
        }
      });
    });
  }

  static bindFilterForms(scope) {
    scope.querySelectorAll('[data-auto-submit]:not([data-theme-bound])').forEach((control) => {
      control.dataset.themeBound = 'true';
      control.addEventListener('change', () => control.form?.requestSubmit());
    });
  }

  static bindTimezoneOffsets(scope) {
    scope.querySelectorAll('[data-timezone-offset]').forEach((input) => {
      input.value = String(new Date().getTimezoneOffset());
    });
  }

  static bindComparisons(scope) {
    scope.querySelectorAll('[data-comparison]:not([data-theme-bound])').forEach((comparison) => {
      comparison.dataset.themeBound = 'true';
      const range = comparison.querySelector('[data-comparison-range]');
      if (!range) return;
      const update = () => comparison.style.setProperty('--comparison-position', `${range.value}%`);
      range.addEventListener('input', update);
      update();
    });
  }
}

class ProductInfo extends HTMLElement {
  connectedCallback() {
    if (this.dataset.themeBound) return;
    this.dataset.themeBound = 'true';
    this.abortController = new AbortController();
    this.addEventListener('change', this.onOptionChange.bind(this));
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  async onOptionChange(event) {
    if (!event.target.matches('[data-option-value-id], select[data-product-option]')) return;

    const selectedOptionValues = Array.from(
      this.querySelectorAll('select [data-option-value-id]:checked, input[data-option-value-id]:checked'),
    )
      .map((option) => option.dataset.optionValueId)
      .filter(Boolean);

    if (!selectedOptionValues.length) return;

    this.abortController.abort();
    this.abortController = new AbortController();
    this.setAttribute('aria-busy', 'true');

    try {
      const url = new URL(this.dataset.productUrl, window.location.origin);
      url.searchParams.set('option_values', selectedOptionValues.join(','));
      url.searchParams.set('section_id', this.dataset.sectionId);
      const response = await fetch(url, { signal: this.abortController.signal });
      if (!response.ok) throw new Error(`Variant section request failed: ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextProductInfo = html.querySelector(`product-info[data-section-id="${this.dataset.sectionId}"]`);
      if (!nextProductInfo) throw new Error('Variant section markup was not returned.');

      const focusId = document.activeElement?.id;
      const selectedVariantId = nextProductInfo.dataset.selectedVariantId;
      this.innerHTML = nextProductInfo.innerHTML;
      this.dataset.selectedVariantId = selectedVariantId;
      VxspeedTheme.init(this);

      if (selectedVariantId) {
        const browserUrl = new URL(this.dataset.productUrl, window.location.origin);
        browserUrl.searchParams.set('variant', selectedVariantId);
        window.history.replaceState({}, '', browserUrl);
      }

      if (focusId) this.querySelector(`#${CSS.escape(focusId)}`)?.focus();
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variantId: selectedVariantId } }));
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.themeBound) return;
    this.dataset.themeBound = 'true';
    const url = this.dataset.url;
    if (!url) return;

    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const recommendationElement = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('product-recommendations');

        if (recommendationElement) this.innerHTML = recommendationElement.innerHTML;
      })
      .catch(() => {
        this.innerHTML = '';
      });
  }
}

if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);
if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

const initializeTheme = () => VxspeedTheme.init(document);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeTheme, { once: true });
else initializeTheme();

document.addEventListener('shopify:section:load', (event) => VxspeedTheme.init(event.target));
